import { Plus, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { Child, SubjectPart, SubjectTotals } from '../lib/types';
import { useParent } from './ParentContext';
import { SubjectField } from '../ui/SubjectField';

/**
 * Hours by subject (#607): a day's recorded hours split by subject on the Attendance tab, and where the school year's
 * time went. Splitting is optional; the day's total is what counts for attendance.
 */

const num = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 2 });

/** The table cell: the day's split as chips, and Split / Edit. */
export function SplitCell({ parts, hours, open, onToggle, disabled }: { parts: SubjectPart[]; hours: number | null; open: boolean; onToggle: () => void; disabled?: boolean }) {
  const split = parts.reduce((a, p) => a + p.hours, 0);
  const left = hours ? Math.round((hours - split) * 100) / 100 : 0;
  return (
    <div className="split-cell">
      {parts.map((p) => (
        <span key={p.subject} className="split-chip">
          {p.subject} {num(p.hours)}
        </span>
      ))}
      {!!hours && parts.length > 0 && left > 0 && <span className="small warn-text">{num(left)} h not split</span>}
      {!!hours && parts.length > 0 && (
        <span className={`small ${parts[0]!.auto ? 'good-text' : 'muted'}`} title={parts[0]!.auto ? 'Split from the planner by itself. Edit and save to make it yours.' : undefined}>
          {parts[0]!.auto ? 'From the planner' : 'Your split'}
        </span>
      )}
      {!!hours && !parts.length && <span className="muted small">Not split</span>}
      {!!hours && (
        <button type="button" className="chip small" onClick={onToggle} disabled={disabled}>
          {open ? 'Close' : parts.length ? 'Edit' : 'Split'}
        </button>
      )}
    </div>
  );
}

/** The editor under a day: subject and hours per line, a fill-from-planner shortcut, and save. */
export function SplitEditor({ child, date, dayLabel, hours, parts, onSaved }: { child: Child; date: string; dayLabel: string; hours: number; parts: SubjectPart[]; onSaved: (parts: SubjectPart[]) => void }) {
  const { toast } = useParent();
  const [rows, setRows] = useState<{ subject: string; hours: string }[]>(parts.length ? parts.map((p) => ({ subject: p.subject, hours: String(p.hours) })) : [{ subject: '', hours: '' }]);
  const [busy, setBusy] = useState(false);
  const subjects = [...new Set([...(child.subjects ?? []), ...rows.map((r) => r.subject).filter(Boolean)])];
  const total = rows.reduce((a, r) => a + (Number(r.hours) || 0), 0);
  const over = total > hours + 0.001;

  async function fill() {
    try {
      const s = await api.get<{ dayHours: number; parts: SubjectPart[] }>(`/api/children/${child.id}/attendance/${date}/suggest`);
      if (!s.parts.length) return toast({ text: `Nothing on ${dayLabel}’s planner or lesson plans to go on.`, tone: 'alert' });
      setRows(s.parts.map((p) => ({ subject: p.subject, hours: String(p.hours) })));
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }
  // Hand the day back to its planner's split (#691): no split of its own.
  async function backToPlanner() {
    setBusy(true);
    try {
      const r = await api.put<{ parts: SubjectPart[] }>(`/api/children/${child.id}/attendance/${date}/subjects`, { parts: [] });
      onSaved(r.parts);
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }
  const auto = parts.length > 0 && !!parts[0]!.auto;
  async function save() {
    setBusy(true);
    try {
      const r = await api.put<{ parts: SubjectPart[] }>(`/api/children/${child.id}/attendance/${date}/subjects`, {
        parts: rows.filter((x) => x.subject.trim() && Number(x.hours) > 0).map((x) => ({ subject: x.subject.trim(), hours: Number(x.hours) })),
      });
      onSaved(r.parts);
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }
  const set = (i: number, patch: Partial<{ subject: string; hours: string }>) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  return (
    <div className="split-editor stack">
      <div className="row-between wrap">
        <strong>
          {dayLabel}’s {num(hours)} hours by subject
          {auto && <span className="good-text small"> · from the planner, until you change it</span>}
        </strong>
        <button type="button" className="btn small" onClick={fill}>
          Fill from {dayLabel}’s planner
        </button>
      </div>
      {rows.map((r, i) => (
        <div key={i} className="split-row">
          {/* The child's subjects as a dropdown, like every other Subject field (#656). */}
          <SubjectField value={r.subject} onChange={(v) => set(i, { subject: v })} extra={subjects} placeholder="Subject" />
          <input type="number" min={0} max={24} step={0.25} value={r.hours} onChange={(e) => set(i, { hours: e.target.value })} placeholder="Hours" aria-label={`Hours of ${r.subject || 'this subject'}`} />
          <button type="button" className="icon-btn" aria-label="Remove" onClick={() => setRows(rows.length > 1 ? rows.filter((_, j) => j !== i) : [{ subject: '', hours: '' }])}>
            <X size={15} />
          </button>
        </div>
      ))}
      <div className="row-between wrap">
        <button type="button" className="link-btn" onClick={() => setRows([...rows, { subject: '', hours: '' }])}>
          <Plus size={14} /> Add a subject
        </button>
        <span className={`small ${over ? 'bad-text' : total < hours ? 'warn-text' : 'muted'}`}>
          {over ? `${num(total)} hours: more than the day’s ${num(hours)}` : total < hours ? `${num(total)} of ${num(hours)} hours split · ${num(hours - total)} not given a subject` : 'All hours split'}
        </span>
      </div>
      <div className="row-gap">
        <button className="btn primary small" disabled={busy || over} onClick={save}>
          {auto ? 'Save as my split' : 'Save'}
        </button>
        {parts.length > 0 && !auto && (
          <button type="button" className="btn small" disabled={busy} onClick={() => void backToPlanner()} title="Remove your split; the planner’s is used when there is one">
            Back to the planner’s
          </button>
        )}
      </div>
    </div>
  );
}

/** The school year's time by subject, with core subjects marked. */
export function TimeBySubject({ child, from, to, version }: { child: Child; from: string; to: string; version: number }) {
  const [t, setT] = useState<SubjectTotals | null>(null);
  useEffect(() => {
    api
      .get<SubjectTotals>(`/api/children/${child.id}/attendance/subjects?from=${from}&to=${to}`)
      .then(setT)
      .catch(() => setT(null));
  }, [child.id, from, to, version]);
  if (!t || (!t.subjects.length && !t.unsplitHours)) return null;
  const rows = [...t.subjects.map((s) => ({ ...s, unsplit: false })), ...(t.unsplitHours ? [{ subject: 'Not split', hours: t.unsplitHours, core: false, unsplit: true }] : [])];
  const max = Math.max(...rows.map((r) => r.hours), 1);
  const total = rows.reduce((a, r) => a + r.hours, 0) || 1;
  if (!t.subjects.length) return null;
  return (
    <section className="card stack">
      <h2>
        Time by subject <span className="muted small">this school year</span>
      </h2>
      <div className="subject-bars">
        {rows.map((r) => (
          <div key={r.subject} className="subject-bar">
            <span>
              {r.subject} {r.core && <span className="tag good small">core</span>}
            </span>
            <div className="subject-track" role="img" aria-label={`${r.subject}: ${num(r.hours)} hours`}>
              <div className={`subject-fill ${r.unsplit ? 'unsplit' : r.core ? 'core' : ''}`} style={{ width: `${(r.hours / max) * 100}%` }} />
            </div>
            <span className="subject-hours">
              <strong>{num(r.hours)}</strong> h <span className="muted small">{Math.round((r.hours / total) * 100)}%</span>
            </span>
          </div>
        ))}
      </div>
      <p className="muted small">Split a day’s hours with Split in the table above. Which subjects are core is set in {child.name}’s Profile → Subjects.</p>
    </section>
  );
}
